import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useForm } from 'react-hook-form'
import { Switch } from '@/components/ui/switch'
import Confetti from 'react-confetti'
import { Sparkles, X, Phone, CheckCircle2, User, AlertCircle, Wallet } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useCoreController } from '@/services/coreController'
import { toast } from 'sonner'
import { Compte } from '@/types/app'
import { appApi } from '@/services/api'
import { storage } from '@/lib/storage'
import { analyticsService } from '@/services/analytics'

interface NewUserOnboardingModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function NewUserOnboardingModal({ open, onOpenChange }: NewUserOnboardingModalProps) {
    const { user, updateUser } = useAuth()
    const navigate = useNavigate()
    const coreController = useCoreController()
    const [showConfetti, setShowConfetti] = useState(false)
    const [step, setStep] = useState<'form' | 'success'>('form')
    const [isLoading, setIsLoading] = useState(false)
    const [notification, setNotification] = useState<{
        isVisible: boolean
        message: string
        type: 'error' | 'success'
    }>({
        isVisible: false,
        message: '',
        type: 'error',
    })

    // Récupérer les bookmakers depuis coreController
    const bookmakers = coreController.appSettingResponse?.compte || []
    const [selectedBookmaker, setSelectedBookmaker] = useState<Compte | null>(null)

    const form = useForm<{
        whatsappNumber: string
        wantsTestDeposit: boolean
        firstBookmaker:     string| null
        bookmakerAccountId: string
        referralCode: string
    }>({
        defaultValues: {
            whatsappNumber: '',
            wantsTestDeposit: false,
            firstBookmaker: null,
            bookmakerAccountId: '',
            referralCode: '',
        },
        mode: 'onSubmit',
    })

    // Watch for changes in wantsTestDeposit
    const wantsTestDeposit = form.watch('wantsTestDeposit')

    // Pré-remplir le numéro si disponible dans le user
    useEffect(() => {
        if (user?.phoneNumber) {
            form.setValue('whatsappNumber', user.phoneNumber.replace('226', ''))
        }

        // Récupérer les infos stockées si elles existent
        const storedOnboarding = localStorage.getItem('userOnboarding')
        if (storedOnboarding) {
            const data = JSON.parse(storedOnboarding)
            if (data.whatsappNumber) form.setValue('whatsappNumber', data.whatsappNumber)
            if (data.firstBookmaker) form.setValue('firstBookmaker', data.firstBookmaker)
            if (data.referralCode) form.setValue('referralCode', data.referralCode)
        }
    }, [user, form])

    const showNotification = (message: string, type: 'error' | 'success' = 'error') => {
        setNotification({ isVisible: true, message, type })
        setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), 5000)
    }

    const onSubmit = async (data: any) => {
        setIsLoading(true)

        try {
            // Validation manuelle
            if (data.wantsTestDeposit) {
                if (!data.firstBookmaker) {
                    showNotification('Veuillez sélectionner un bookmaker', 'error')
                    setIsLoading(false)
                    return
                }
                if (!data.bookmakerAccountId || data.bookmakerAccountId.length < 3) {
                    showNotification('Veuillez entrer votre ID de compte (min 3 chiffres)', 'error')
                    setIsLoading(false)
                    return
                }
                if (!/^[0-9]+$/.test(data.bookmakerAccountId)) {
                    showNotification('L\'ID doit contenir que des chiffres', 'error')
                    setIsLoading(false)
                    return
                }
            }

            // Stocker les infos dans localStorage
            // Si l'utilisateur a fourni un code de parrainage et qu'il n'a pas déjà de commercialId
            const effectiveCommercialId = user?.commercialId || coreController.commercialId || ''
            const shouldUseReferralCode = !effectiveCommercialId && data.referralCode?.trim()

            // Utiliser le code de parrainage comme commercialId si aucun commercialId n'existe
            if (shouldUseReferralCode) {
                coreController.setCommercialId(data.referralCode.trim())
                console.log('Setting commercialId from referral code:', data.referralCode.trim())
            }

            const onboardingData = {
                whatsappNumber: `226${data.whatsappNumber}` || '',
                wantsTestDeposit: data.wantsTestDeposit,
                firstBookmaker: selectedBookmaker?.type || data.firstBookmaker,
                bookmakerAccountId: data.bookmakerAccountId || '',
                userId: user?.id || '',
                commercialId: shouldUseReferralCode ? data.referralCode.trim() : effectiveCommercialId,
                email: user?.email || '',
                displayName: user?.displayName || '',
                // N'inclure le referralCode dans les données envoyées que si commercialId n'était pas déjà trouvé
                ...(shouldUseReferralCode ? { referralCode: data.referralCode.trim() } : {}),
                timestamp: new Date().toISOString(),
            }
            console.log('Onboarding data to store and send:', onboardingData)
            console.log('Selected bookmaker:', selectedBookmaker)


            localStorage.setItem('userOnboarding', JSON.stringify({
                ...onboardingData,
                referralCode: data.referralCode || ''
            }))


            // Notifier l'admin du nouvel utilisateur
            await notifyNewUserOnboarding(onboardingData)

            // Marquer l'onboarding comme complété dans localStorage et user
            localStorage.setItem('onboardingCompleted', 'true')

            // Si l'utilisateur veut faire un test dépôt
            if (data.wantsTestDeposit && data.firstBookmaker && data.bookmakerAccountId) {
                const bookmakerUserId = data.bookmakerAccountId
                const selectedBookmakerToUse = bookmakers.find((b: Compte) => b.type === data.firstBookmaker)

                // Save user ID to storage
                storage.write(`user_id_${selectedBookmakerToUse?.type}`, bookmakerUserId)

                // Track test deposit
                await analyticsService.trackDepositInitiated({
                    amount: 100,
                    accountType: selectedBookmakerToUse?.type ??"1XBET",
                    commercialId: coreController.commercialId || undefined,
                })

                // Verify account ID and get user name (comme DepositPage)
                const userName = await appApi.getUserName(bookmakerUserId, selectedBookmakerToUse?.id ?? bookmakers[0]?.id ?? '')

                if (userName === null) {
                    showNotification(
                        `Votre ID ${selectedBookmakerToUse?.type} semble invalide. Veuillez vérifier l'identifiant ${selectedBookmakerToUse?.type} saisi.`
                    )
                    analyticsService.trackError(new Error('Invalid account ID'), 'onboarding_account_verification')
                    setIsLoading(false)
                    return
                }

                if (userName.trim() === '') {
                    showNotification(`Avertissement : Aucun nom associé à votre ID ${selectedBookmakerToUse?.type}.`)
                }

                // Afficher l'animation confetti
                setShowConfetti(true)
                setStep('success')

                toast.success('Bienvenue chez EASYSERVICE EXPRESS! 🎉')

                // Naviguer vers la page de paiement avec 100 FCFA après 2 secondes
                setTimeout(() => {
                 
                    navigate('/payment', {
                        state: {
                            type: 'deposit',
                            amount: 100,
                            accountType: selectedBookmakerToUse?.type ??"1XBET",
                            userId: bookmakerUserId,
                            userName: userName || 'Utilisateur',
                            accountId: selectedBookmakerToUse?.id ?? bookmakers[0]?.id ?? '',
                            commercialId: coreController.commercialId,
                        },
                    })
                }, 2000)
                setTimeout(() => {
                    setShowConfetti(false)
                       onOpenChange(false)
                }, 5000)
            } else {
                // Pas de test dépôt - juste collecter le WhatsApp
                setShowConfetti(true)
                setStep('success')

                toast.success('Bienvenue chez EASYSERVICE EXPRESS! 🎉')

                // Fermer le modal après 2 secondes
                setTimeout(() => {
                    setShowConfetti(false)
                    onOpenChange(false)
                }, 2000)
            }
        } catch (error) {
            console.error('Erreur lors de l\'onboarding:', error)
            showNotification('Une erreur est survenue. Veuillez réessayer.')
            analyticsService.trackError(error as Error, 'onboarding_submission')
        } finally {
            setIsLoading(false)
        }
    }

    const handleSkip = () => {
        localStorage.setItem('onboardingCompleted', 'true')
        updateUser({ onboardingCompleted: true })
        onOpenChange(false)
    }

    const handleBookmakerChange = (bookmakerType: string) => {
        if (!bookmakerType) {
            setSelectedBookmaker(null)
            return
        }
        const bookmaker = bookmakers.find((b: Compte) => b.type === bookmakerType)
        if (bookmaker) {
            setSelectedBookmaker(bookmaker)
            coreController.setAccount(bookmaker)
        }
    }

    const getThemeColor = (): string => {
        if (!selectedBookmaker) return '#0052cc'
        switch (selectedBookmaker.type) {
            case '1XBET':
                return '#002366'
            case 'BETWINNER':
                return '#0052cc'
            case 'MELBET':
                return '#0052cc'
            default:
                return '#0052cc'
        }
    }

    return (
        <>
            {showConfetti && (
                <Confetti
                    width={window.innerWidth}
                    height={window.innerHeight}
                    recycle={false}
                    numberOfPieces={500}
                    gravity={0.3}
                    colors={['#0052cc', '#003d99', '#0052cc', '#003d99', '#002366']}
                />
            )}

            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                    <button
                        onClick={() => onOpenChange(false)}
                        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none z-10"
                    >
                        <X className="h-4 w-4" />
                        <span className="sr-only">Fermer</span>
                    </button>

                    {step === 'form' ? (
                        <>
                            <DialogHeader className="text-center">
                                <div className="flex justify-center mb-2">
                                    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center animate-pulse">
                                        <Sparkles className="h-8 w-8 text-foreground" />
                                    </div>
                                </div>
                                <DialogTitle className="text-2xl font-bold">
                                    Bienvenue chez EASYSERVICE EXPRESS 🎉
                                </DialogTitle>
                                <DialogDescription className="text-base pt-2">
                                    Laissez-nous votre numéro WhatsApp pour vous contacter en cas de gains
                                </DialogDescription>
                            </DialogHeader>

                            {/* Notification */}
                            {notification.isVisible && (
                                <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${
                                    notification.type === 'error' ? 'bg-destructive/10 text-destructive' : 'bg-green-50 text-green-700'
                                }`}>
                                    {notification.type === 'error' ? <AlertCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                                    <span>{notification.message}</span>
                                </div>
                            )}

                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                                <div className="space-y-4">
                                    <div className="grid gap-2">
                                        <Label htmlFor="whatsapp" className="text-sm font-medium flex items-center gap-2">
                                            <Phone className="h-4 w-4 text-green-500" />
                                            Numéro WhatsApp *
                                        </Label>
                                        <div className="flex items-center">
                                            <span className="bg-muted px-3 py-2.5 rounded-l-md border border-r-0 border-input text-sm font-medium">
                                                +226
                                            </span>
                                            <Input
                                                id="whatsapp"
                                                placeholder="70766767"
                                                className="rounded-l-none"
                                                {...form.register('whatsappNumber')}
                                            />
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            Pour vous contacter en cas de gains ou difficultés
                                        </p>
                                    </div>

                                    <div className="grid gap-2">
                                        <Label htmlFor="referralCode" className="text-sm font-medium flex items-center gap-2">
                                            Code de parrainage
                                        </Label>
                                        <Input
                                            id="referralCode"
                                            placeholder="Ex: REF123"
                                            {...form.register('referralCode')}
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Optionnel - Si vous avez un code de parrainage
                                        </p>
                                    </div>

                                    {/* Switch pour le dépôt test optionnel */}
                                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                        <div className="flex-1">
                                            <Label htmlFor="test-deposit-switch" className="text-sm font-medium cursor-pointer">
                                                Faire un test dépôt?
                                            </Label>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Testez notre service avec 100 FCFA (optionnel)
                                            </p>
                                        </div>
                                        <Switch
                                            id="test-deposit-switch"
                                            checked={form.watch('wantsTestDeposit')}
                                            onCheckedChange={(checked) => form.setValue('wantsTestDeposit', checked)}
                                        />
                                    </div>

                                    {/* Champs pour le dépôt test - affichés seulement si le switch est activé */}
                                    {wantsTestDeposit && (
                                        <div className="space-y-3 pt-2 border-t">
                                            <div className="rounded-xl p-4 border bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                                                <p className="text-center text-sm text-muted-foreground mb-2">
                                                    Montant du test
                                                </p>
                                                <div className="flex items-center justify-center gap-2">
                                                    <Wallet className="w-6 h-6 text-purple-600" />
                                                    <p className="text-2xl font-bold text-purple-600">
                                                        100 FCFA
                                                    </p>
                                                </div>
                                                <p className="text-center text-xs text-gray-500 mt-2">
                                                    Testez la rapidité de notre service
                                                </p>
                                            </div>

                                            <div className="grid gap-2">
                                                <Label htmlFor="bookmaker" className="text-sm font-medium">
                                                    Bookmaker *
                                                </Label>
                                                <select
                                                    id="bookmaker"
                                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                    {...form.register('firstBookmaker', {
                                                        onChange: (e) => handleBookmakerChange(e.target.value)
                                                    })}
                                                >
                                                    <option value="">Sélectionnez un bookmaker</option>
                                                    {bookmakers.map((bookmaker: Compte) => (
                                                        <option key={bookmaker.id} value={bookmaker.type}>
                                                            {bookmaker.type}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="grid gap-2">
                                                <Label htmlFor="bookmakerAccountId" className="text-sm font-medium flex items-center gap-2">
                                                    <User className="h-4 w-4" style={{ color: getThemeColor() }} />
                                                    ID du compte {selectedBookmaker?.type || ''} *
                                                </Label>
                                                <Input
                                                    id="bookmakerAccountId"
                                                    type="text"
                                                    inputMode="numeric"
                                                    placeholder="Entrez votre ID de compte"
                                                    className={selectedBookmaker ? 'border-2' : ''}
                                                    style={selectedBookmaker ? { borderColor: getThemeColor() } : {}}
                                                    {...form.register('bookmakerAccountId')}
                                                />
                                                {selectedBookmaker && (
                                                    <p className="text-xs text-muted-foreground">
                                                        Entrez votre ID {selectedBookmaker.type} pour le test
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <DialogFooter className="flex gap-2 sm:gap-2 pt-4">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={handleSkip}
                                        disabled={isLoading}
                                    >
                                        Passer
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={isLoading}
                                        className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                                    >
                                        {isLoading ? 'Traitement...' : wantsTestDeposit ? 'Tester le service' : 'Commencer'}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <div className="flex justify-center mb-4">
                                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-green-500 to-brand-blue flex items-center justify-center animate-bounce">
                                    <CheckCircle2 className="h-10 w-10 text-foreground" />
                                </div>
                            </div>
                            <DialogTitle className="text-2xl font-bold mb-2">
                                Bienvenue! 🎊
                            </DialogTitle>
                            <DialogDescription className="text-base">
                                {wantsTestDeposit ? 'Redirection vers le paiement de test...' : 'Vous êtes maintenant prêt à découvrir EASYSERVICE EXPRESS'}
                            </DialogDescription>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}

// API pour notifier l'admin d'un nouvel utilisateur qui complète l'onboarding
interface OnboardingData {
    userId: string
    commercialId: string
    whatsappNumber: string
    wantsTestDeposit: boolean
    firstBookmaker: string
    bookmakerAccountId: string
    email: string
    displayName: string
    referralCode?: string
    timestamp: string
}

export async function notifyNewUserOnboarding(data: OnboardingData): Promise<void> {
    try {

        // Appel à l'endpoint Directus pour notifier l'admin
        const response = await fetch(`https://n8n-dc4k448gogc4cgg8gss8008c.infra.dongobada.com/webhook/new-customers`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        })

        if (!response.ok) {
            console.warn('Could not notify admin about new user onboarding:', await response.text())
        }
    } catch (error) {
        console.error('Error notifying admin:', error)
        // Ne pas bloquer l'UX si la notification échoue
    }
}
